// Libraries
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';
import { Link } from 'react-router-dom';
import { skuPath } from 'src/utils/paths';
import Grid from 'src/components/HTKit/Grid/Grid';
import Button, { THEMES } from 'src/components/HTKit/Forms/Button';
import ImageGallery from 'src/components/Elements/ImageGallery';
import StarRating from 'src/components/Elements/StarRating';
import Tooltip from 'src/components/HTKit/Elements/Tooltip';
import {
  splitioConstants,
  SplitTreatmentToggle,
  useSplitIOTreatment,
} from 'src/components/SplitIO';
import { ProductPrice, ProductPriceLegacyMain } from 'src/components/Product/ProductPrice';
import ProductSubscriptionupsell from 'src/components/Product/ProductSubscriptionUpsell';
import ProductHeaderTrigger from 'src/containers/ProductPage/ProductHeaderTrigger';
import useWindowType, { WIN_SM } from 'src/hooks/useWindowType';
import ProductInstallationOptions from './ProductInstallationOptions';
import ProductHeaderFeatures from './ProductHeaderFeatures';
import ProductInfo from './ProductInfo';
import ProductSpecialistChat from './ProductSpecialistChat';

// Styles
import styles from './styles.scss';

const ProductHeader = ({
  service,
  gallery,
  onBook,
  onRating,
  className,
  rating,
  ratingCount,
  serviceSkuSeoName,
  priceDetails,
  product,
  handleUpsellToggle,
  handleUpsellLearnMore,
  topNavAndHeaderHeight,
  showInstallationOnlyOption,
  ...rest
}) => {
  const { isProductVariant } = rest;
  const amountForUpsell = isProductVariant
    ? get(product, 'extra.prices.memberPriceDifference.productWithInstallation', null)
    : get(product, 'extra.prices.memberPriceDifference.product', null);

  const { splitTreatment } = useSplitIOTreatment(
    splitioConstants.SPLITIONAME_PRODUCT_PAGE_MEMBERSHIP_UPSELL,
  );

  /* Product Promo Start */
  const windowType = useWindowType();
  const isMobileImage = [WIN_SM].includes(windowType);

  const { splitConfig, splitTreatment: splitProductPromoTreatment } = useSplitIOTreatment(
    splitioConstants.SPLITNAME_PRODUCT_PAGE_PROMO_IMAGE,
    {skuId: product.id}
  );

  const productPromoImageStyle = cn({
    [styles.productPromoImageMobile]: isMobileImage,
    [styles.productPromoImageDesktop] : !isMobileImage,
  });

  const hasPromoTreatment = splitProductPromoTreatment === splitioConstants.ON;
  const productPromoImage = isMobileImage ? splitConfig.mobileImage : splitConfig.desktopImage;
  const PromoImage = (hasPromoTreatment && productPromoImage) ? <div className={productPromoImageStyle}><img src={productPromoImage} alt="Product Promo Discount" />&nbsp;</div> : null;
  /* Product Promo End */

  const showNewUpsellTreatment = splitTreatment !== splitioConstants.OFF;
  const showMembershipUpsellComponent =
    get(product, 'extra.showUpsell', false) && showNewUpsellTreatment;

  // offsetTriggerValue is an eyeballed value meant to trigger after the button disappears from the viewport
  const offsetTriggerValue = showMembershipUpsellComponent ? 20 : -140;
  const triggerStyle = {
    top: `-${topNavAndHeaderHeight + offsetTriggerValue}px`,
  }

  return (
    <section className={className}>
      <Grid.FullWidth>
        <Grid.Row>
          <Grid.Column sm={4} md={8} lg={7} classes="paddingTop-small positionRelative">
            <ImageGallery componentInsert={PromoImage} gallery={gallery} className={styles.imageGallery} />
          </Grid.Column>
          <Grid.Column sm={4} md={8} lg={5} classes="paddingTop-small">
            <p className={cn('p2 paddingBottom-tiny', styles.brand)}>{service.brandName}</p>
            <h4 className="marginBottom-tiny1">{service.name}</h4>
            {rating && (
              <Tooltip content="HelloTech installation service reviews">
                <StarRating
                  stars={rating}
                  count={ratingCount}
                  onClick={onRating}
                  className={styles.rating}
                />
              </Tooltip>
            )}
            {showNewUpsellTreatment ? (
              <ProductPrice
                priceStyles="marginBottom-tiny"
                service={service}
                {...rest}
                showAffirm
                showRetailPrice
                showPriceGuarantee
              />
            ) : (
              <ProductPriceLegacyMain service={service} />
            )}

            {showMembershipUpsellComponent && (
              <ProductSubscriptionupsell
                service={service}
                selectedPlanId={product.extra.selectedPlanId}
                plansInfo={product.extra.plansInfo}
                amount={amountForUpsell}
                onToggle={handleUpsellToggle}
                onLearnMore={handleUpsellLearnMore}
                className={styles.upsellContainer}
              />
            )}

            {priceDetails && (
              <>
                <ProductInstallationOptions priceDetails={priceDetails} {...rest} />
                {showInstallationOnlyOption && (
                  <p className={cn('p2 text-weight-med paddingBottom-small1')}>
                    Need installation only? <Link to={skuPath(serviceSkuSeoName)}>Learn More</Link>
                  </p>
                )}
              </>
            )}
            {showMembershipUpsellComponent && (
              <Button disabled theme={THEMES.V2PRIMARY} onClick={onBook} className={styles.ctaButton}>
                Out of Stock
              </Button>
            )}

            {!showMembershipUpsellComponent && (
              <div className={cn(styles.borderTop, 'marginBottom-small')} />
            )}

            <ProductHeaderFeatures />

            <ProductHeaderTrigger style={triggerStyle} />

            {!showMembershipUpsellComponent && (
              <Button
                disabled
                theme={THEMES.V2PRIMARY}
                onClick={onBook}
                className={cn(styles.ctaButton, styles.memberUpsellHidden)}
              >
                Out of Stock
              </Button>
            )}

            <SplitTreatmentToggle splitName={splitioConstants.SPLITIONAME_PRODUCT_SPECIALIST_CHAT}>
              <ProductSpecialistChat
                service={service}
                className={cn('paddingTop-small1 marginBottom-medium', styles.borderTop)}
                {...rest}
              />
            </SplitTreatmentToggle>

            <ProductInfo bullets={service.skuBullets} className="marginTop-medium" />
          </Grid.Column>
        </Grid.Row>
      </Grid.FullWidth>
    </section>
  );
};

ProductHeader.propTypes = {
  service: PropTypes.shape({
    name: PropTypes.string,
    brandName: PropTypes.string,
    skuBullets: PropTypes.array,
  }),
  priceDetails: PropTypes.object,
  rating: PropTypes.number,
  ratingCount: PropTypes.number,
  gallery: PropTypes.array,
  onBook: PropTypes.func.isRequired,
  onRating: PropTypes.func.isRequired,
  className: PropTypes.string,
  serviceSkuSeoName: PropTypes.string,
  product: PropTypes.shape({
    extra: PropTypes.object,
    id: PropTypes.number
  }),
  handleUpsellToggle: PropTypes.func,
  handleUpsellLearnMore: PropTypes.func,
  topNavAndHeaderHeight: PropTypes.number,
  showInstallationOnlyOption: PropTypes.bool,
};

ProductHeader.defaultProps = {
  brandName: 'Chamberlain',
  handleUpsellToggle: () => {},
  handleUpsellLearnMore: () => {},
  topNavAndHeaderHeight: 0,
  showInstallationOnlyOption: true,
};

export default memo(ProductHeader);

